import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Popover,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Icon } from "@iconify/react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import StateDistrictFilter from "./geographyFilter";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

// Clear Filters + Export CSV. Rendered inside the filters row on desktop and,
// on mobile, below the filters card (see the dashboards) via the `sx` override.
export const DashboardFilterActions = ({
  handleClearFilters,
  isDefault,
  handleExportCSV,
  sx,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: { xs: "flex-start", md: "flex-end" },
      gap: 2,
      alignItems: "center",
      ...sx,
    }}
  >
    {/* CLEAR FILTER */}
    {!isDefault && (
      <Box
        onClick={handleClearFilters}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "3px",
          cursor: "pointer",
          color: "#5751E9",
          fontWeight: 600,
          fontSize: "16px",
          textDecoration: "underline",
        }}
      >
        <CloseIcon sx={{ fontSize: 14, color: "#5751E9" }} />
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "15px",
            textDecoration: "underline",
          }}
        >
          Clear Filters
        </Typography>
      </Box>
    )}

    {/* EXPORT */}
    <Typography
      variant="body1"
      sx={{
        color: "#5650E7",
        fontSize: 15,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        textDecoration: "underline",
      }}
      onClick={handleExportCSV}
    >
      <FileDownloadOutlinedIcon sx={{ mr: 1, width: 20, height: 20 }} />
      Export CSV
    </Typography>
  </Box>
);

const DashboardFiltersRow = ({
  // date
  selectedDateTitle,
  selectedDateFilter,
  setSelectedDateFilter,
  setSelectedDateTitle,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  dateMenuOpen,
  dateAnchorEl,
  handleOpenDateMenu,
  handleCloseDateMenu,
  applyCustomDates,

  // geo
  geoFilter,
  setGeoFilter,

  // centres
  allCentres,
  selectedCentres,
  setSelectedCentres,
  isMdUp,
  DropdownIcon,

  // export
  handleExportCSV,

  // clear filters
  handleClearFilters,
  isDefault,
}) => {
  const [search, setSearch] = useState("");

  const filteredCentres = useMemo(() => {
    return allCentres.filter((centre) =>
      String(centre?.label || "")
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [allCentres, search]);
  return (
    <>
      <Box
        sx={{
          mt: 0,
          mb: 0,
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 1.5,
        }}
      >
        {/* LEFT SECTION – Filters */}
        <Box
          sx={{
            width: { xs: "100%", md: "70%" },
            display: "flex",
            flexDirection: "row", // changed
            gap: 2,
            alignItems: "center",
            flexWrap: "nowrap", // changed
            overflowX: { xs: "auto", md: "visible" }, // changed

            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Grid container spacing={2} alignItems="center" wrap="nowrap">
            <Grid item>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row", // changed
                  gap: 1.5,
                  flexWrap: "nowrap", // changed
                  alignItems: "center",
                  pb: 0,
                }}
              >
                {/* Date dropdown */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleOpenDateMenu}
                      endIcon={
                        <Icon
                          icon="eva:arrow-ios-downward-fill"
                          width="18"
                          height="18"
                        />
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        border: "1px solid var(--app-border)",
                        height: "40px",
                        minWidth: "max-content",
                        flexShrink: 0,
                        justifyContent: "space-between",
                        color: "var(--app-text) !important",
                        fontSize: "14px !important",
                        fontweight: 500,
                        px: 2,
                        gap: 1,
                      }}
                    >
                      {selectedDateTitle}
                    </Button>

                    <Popover
                      open={dateMenuOpen}
                      onClose={handleCloseDateMenu}
                      anchorEl={dateAnchorEl}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 0.5,
                            borderRadius: "12px",
                            border: "1px solid var(--app-border)",
                            boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                          },
                        },
                      }}
                    >
                      <Box sx={{ p: 2, width: 320 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 700 }}
                        >
                          Quick ranges
                        </Typography>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {[
                            ["Today", "today"],
                            ["Last 7 Days", "last_7_days"],
                            ["Last 30 Days", "last_30_days"],
                            ["Last 3 Months", "last_3_months"],
                            ["Last 6 Months", "last_6_months"],
                            ["Last 12 Months", "last_12_months"],
                          ].map(([label, value]) => (
                            <Button
                              key={value}
                              size="small"
                              onClick={() => {
                                setSelectedDateFilter(value);
                                setSelectedDateTitle(label);
                                setFromDate(null);
                                setToDate(null);
                              }}
                              sx={{
                                textTransform: "none",
                                borderRadius: "9px",
                                border: "1px solid var(--app-border)",
                                height: "42px",
                                color: "var(--app-text)",
                                bgcolor:
                                  selectedDateFilter === value
                                    ? "var(--app-surface-2)"
                                    : "transparent",
                              }}
                            >
                              {label}
                            </Button>
                          ))}
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 700 }}
                        >
                          Custom range
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                          <DatePicker
                            label="From"
                            value={fromDate}
                            onChange={(v) => setFromDate(v)}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                          <DatePicker
                            label="To"
                            value={toDate}
                            onChange={(v) => setToDate(v)}
                            minDate={fromDate || undefined}
                            maxDate={new Date()}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                          }}
                        >
                          <Button size="small" onClick={handleCloseDateMenu}>
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={applyCustomDates}
                            disabled={!fromDate || !toDate}
                            sx={{
                              backgroundColor: "#5751E9",
                            }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Box>
                    </Popover>
                  </Box>
                </LocalizationProvider>

                <Box sx={{ minWidth: 150, flexShrink: 0 }}>
                  <StateDistrictFilter
                    key={`${geoFilter.state}-${geoFilter.district}`}
                    onChange={(s, d) => setGeoFilter({ state: s, district: d })}
                    initialState={geoFilter.state}
                    initialDistrict={geoFilter.district}
                  />
                </Box>

                {/* Centres Multi-Select */}
                <Box
                  sx={{
                    minWidth: 150,
                    flexShrink: 0,
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: 140,
                      backgroundColor: "var(--app-surface)",
                      border: "1px solid var(--app-border)",
                      height: 40,
                      borderRadius: "8px",
                      justifyContent: "center",
                      "& .MuiOutlinedInput-root": {
                        fontSize: 14,
                        fontWeight: 500,
                        color: "var(--app-text)",
                        border: "none",
                        "& fieldset": { border: "none" },
                      },
                      "& .MuiSelect-icon": {
                        top: "50%",
                        transform: "translateY(-50%)",
                      },
                    }}
                  >
                    <Select
                      multiple
                      displayEmpty
                      value={selectedCentres.map((c) => c.value)}
                      IconComponent={DropdownIcon}
                      onOpen={() => setSearch("")}
                      onChange={(e) => {
                        const vals = e.target.value;

                        // ALL toggle
                        if (vals.includes("all")) {
                          if (selectedCentres.length === allCentres.length) {
                            setSelectedCentres([]);
                          } else {
                            setSelectedCentres(allCentres);
                          }
                          return;
                        }

                        // NONE selected
                        if (!vals || vals.length === 0) {
                          setSelectedCentres([]);
                          return;
                        }

                        // NORMAL selection
                        const newSelection = allCentres.filter((opt) =>
                          vals.includes(opt.value),
                        );

                        setSelectedCentres(newSelection);
                      }}
                      renderValue={(selected) => {
                        // NONE selected
                        if (selected.length === 0) {
                          return "All Centres";
                        }

                        // ALL selected
                        if (selected.length === allCentres.length) {
                          return "All Centres";
                        }

                        // MULTIPLE selected
                        if (selected.length > 1) {
                          return `${selected.length} selected`;
                        }

                        // SINGLE selected
                        return allCentres
                          .filter((opt) => selected.includes(opt.value))
                          .map((opt) => opt.label)
                          .join(", ");
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 48 * 8, // shows 8 items only
                            overflowY: "auto",
                            mt: 0.5,
                            borderRadius: "12px",
                            border: "1px solid var(--app-border)",
                            boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                            "& .MuiMenuItem-root": { fontSize: 14, fontFamily: "figtree" },
                          },
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        MenuListProps: {
                          autoFocusItem: false,
                        },
                      }}
                      sx={{
                        width: "100%",
                        height: 40,

                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },

                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          fontSize: 14,
                          fontWeight: 500,
                          color: "var(--app-text)",
                          padding: "8px 12px",
                        },

                        "& .MuiSelect-icon": {
                          right: 10,
                        },
                      }}
                    >
                      {/* SEARCH */}
                      <Box
                        sx={{
                          p: 1,
                          position: "sticky",
                          top: 0,
                          background: "var(--app-surface)",
                          zIndex: 10,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TextField
                          autoFocus
                          fullWidth
                          size="small"
                          placeholder="Search Centre"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: 38,
                              borderRadius: "12px",
                              backgroundColor: "var(--app-surface)",

                              "& input": {
                                padding: "12px 0px",
                                fontSize: "15px",
                                fontWeight: 400,
                                color: "var(--app-text)",
                              },

                              "& input::placeholder": {
                                color: "var(--app-text-muted)",
                                opacity: 1,
                                fontSize: "15px",
                              },

                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--app-border)",
                              },

                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                border: "1px solid var(--app-border)",
                              },

                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  border: "1px solid var(--app-border)",
                                },

                              "&.Mui-focused": {
                                boxShadow: "none",
                              },
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon
                                  sx={{
                                    color: "var(--app-text-muted)",
                                    fontSize: 24,
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {/* ALL CENTRES */}
                      <MenuItem
                        value="all"
                        sx={{
                          minHeight: 42,

                          "&.Mui-selected": {
                            backgroundColor: "transparent !important",
                          },

                          "&.Mui-selected:hover": {
                            backgroundColor: "transparent !important",
                          },

                          "&:hover": {
                            backgroundColor: "var(--app-surface-2)",
                          },
                        }}
                      >
                        <Checkbox
                          checked={selectedCentres.length === allCentres.length}
                          indeterminate={
                            selectedCentres.length > 0 &&
                            selectedCentres.length < allCentres.length
                          }
                          size="small"
                          sx={{
                            color: "var(--app-border)",

                            "&.Mui-checked": {
                              color: "#5751E9",
                            },

                            "& .MuiSvgIcon-root": {
                              borderRadius: "8px",
                              fontSize: 20,
                            },
                          }}
                        />

                        <ListItemText
                          primary="All Centres"
                          primaryTypographyProps={{
                            fontSize: 14,
                            noWrap: true,
                          }}
                        />
                      </MenuItem>

                      {/* OPTIONS */}
                      {filteredCentres.length > 0 ? (
                        filteredCentres.map((centre) => (
                          <MenuItem
                            key={centre.value}
                            value={centre.value}
                            sx={{
                              minHeight: 42,

                              "&.Mui-selected": {
                                backgroundColor: "transparent !important",
                              },

                              "&.Mui-selected:hover": {
                                backgroundColor: "transparent !important",
                              },

                              "&:hover": {
                                backgroundColor: "var(--app-surface-2)",
                              },
                            }}
                          >
                            <Checkbox
                              checked={selectedCentres.some(
                                (c) => c.value === centre.value,
                              )}
                              size="small"
                              sx={{
                                color: "var(--app-border)",

                                "&.Mui-checked": {
                                  color: "#5751E9",
                                },

                                "& .MuiSvgIcon-root": {
                                  borderRadius: "8px",
                                  fontSize: 20,
                                },
                              }}
                            />

                            <ListItemText
                              primary={centre.label}
                              primaryTypographyProps={{
                                fontSize: 14,
                                noWrap: true,
                              }}
                            />
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <ListItemText
                            primary="No results found"
                            primaryTypographyProps={{
                              fontSize: 14,
                              color: "var(--app-text-muted)",
                            }}
                          />
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* RIGHT SECTION – desktop only (mobile renders these below the card) */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: { xs: "none", md: "block" } }}
        >
          <DashboardFilterActions
            handleClearFilters={handleClearFilters}
            isDefault={isDefault}
            handleExportCSV={handleExportCSV}
          />
        </Grid>
      </Box>
    </>
  );
};

export default DashboardFiltersRow;
